import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { getProjectServices } from '../services/SandboxService';
import { isUserActive, USER_RETURN_EVENT } from '../utils/userActivity';

/**
 * The state of one project's services (`GET /api/v1/projects/:id/services`), refreshed every `intervalMs` while the
 * page is visible. Unlike `useSandbox` (the caller's own pod), this reaches the pod the project runs in, so it is
 * right for a project shared with the caller too. Returns `{ services, role, canRun, unavailable, known, loaded, at,
 * refresh }`: `services` keeps its last good value while a refresh fails; `canRun` is false for a viewer (state, no
 * controls); `at` when the answer arrived (ms).
 *
 * One answer per project for the whole page: every caller (the header's "Running 1/1", the Preview tab's watchdog,
 * the bottom bar through it) reads the same store, and a refresh by one is seen by all at once, so they never
 * disagree about whether a service runs. Requests already in flight are shared, not repeated.
 */

const stores = new Map(); // projectId -> { snapshot: { answer, at, loaded }, listeners: Set, inflight: Promise|null }

function storeFor(projectId) {
  let store = stores.get(projectId);
  if (!store) {
    store = { snapshot: { answer: null, at: 0, loaded: false }, listeners: new Set(), inflight: null };
    stores.set(projectId, store);
  }
  return store;
}

async function refreshStore(projectId) {
  const store = storeFor(projectId);
  if (store.inflight) { return store.inflight; }
  store.inflight = (async() => {
    try {
      const answer = await getProjectServices(projectId);
      store.snapshot = { answer, at: Date.now(), loaded: true };
      store.listeners.forEach((listener) => listener());
      return answer;
    } catch {
      return null; // a blip: keep the last answer
    } finally {
      store.inflight = null;
    }
  })();
  return store.inflight;
}

const EMPTY = { answer: null, at: 0, loaded: false };
const NO_NAMES = [];
const noop = () => () => {};

/**
 * The project's services answer outside React (Canvas's preview loader): the store's when it is at most `maxAgeMs`
 * old, else a fresh one (shared with any refresh in flight, and seen by every hook caller). `null` when vfs-service
 * cannot answer (not configured, too old, or a failed call with nothing known before).
 * @param {string} projectId
 * @param {{ maxAgeMs?: number }} [options]
 */
export async function loadProjectServices(projectId, { maxAgeMs = 3000 } = {}) {
  if (!projectId) { return null; }
  const store = storeFor(projectId);
  // `maxAgeMs` 0: always a new answer (shared with a request already in flight).
  if (store.snapshot.loaded && Date.now() - store.snapshot.at < maxAgeMs) { return store.snapshot.answer; }
  const answer = await refreshStore(projectId);
  return answer ?? (store.snapshot.loaded ? store.snapshot.answer : null);
}

/**
 * The first real answer about the project's services, waiting for it: on a cold page load the first calls can fail
 * (the session or the config not ready yet), and "no answer yet" must never be read as "no session" (that started a
 * stopped app). Retries every `retryMs` until an answer arrives or `timeoutMs` passes; resolves to the answer (null
 * when vfs-service has no such route), or `undefined` when nothing could be learned in time.
 * @param {string} projectId
 * @param {{ maxAgeMs?: number, timeoutMs?: number, retryMs?: number }} [options]
 */
export async function waitForProjectServices(projectId, { maxAgeMs = 3000, timeoutMs = 8000, retryMs = 500 } = {}) {
  if (!projectId) { return undefined; }
  const deadline = Date.now() + timeoutMs;
  let age = maxAgeMs;
  let last;
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    await loadProjectServices(projectId, { maxAgeMs: age });
    // The store's own snapshot: the one every caller (the header too) reads and the services call fills.
    const { loaded, answer } = storeFor(projectId).snapshot;
    if (loaded) {
      // "Services unavailable" (the runner not answering, e.g. right after the sandbox wakes) says nothing about the
      // service: ask again until a real answer or the timeout.
      if (!answer?.servicesUnavailable) { return answer; }
      last = answer;
    }
    if (Date.now() >= deadline) { return last; }
    age = 0;
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => { setTimeout(resolve, retryMs); });
  }
}

/** Refresh the project's services now (after a start or stop), for every caller. */
export function refreshProjectServices(projectId) {
  return projectId ? refreshStore(projectId) : Promise.resolve(null);
}

/** Tests only: forget every project's answer. */
export function resetProjectServicesStore() {
  stores.clear();
}

export default function useProjectServices(projectId, { intervalMs = 5000, enabled = true } = {}) {
  const subscribe = useCallback((listener) => {
    if (!projectId) { return () => {}; }
    const store = storeFor(projectId);
    store.listeners.add(listener);
    return () => store.listeners.delete(listener);
  }, [projectId]);
  const snapshot = useSyncExternalStore(
    projectId ? subscribe : noop,
    () => (projectId ? storeFor(projectId).snapshot : EMPTY),
    () => EMPTY,
  );

  const refresh = useCallback(async() => {
    if (!projectId) { return null; }
    return refreshStore(projectId);
  }, [projectId]);

  useEffect(() => {
    if (!enabled || !projectId) { return undefined; }
    refresh();
    const timer = setInterval(() => {
      // Hidden, or nobody at the screen: ask nothing (every request keeps the sandbox from sleeping).
      if (document.visibilityState !== 'visible' || !isUserActive()) { return; }
      // Another caller refreshed the same project a moment ago: its answer is everyone's.
      if (Date.now() - storeFor(projectId).snapshot.at < intervalMs / 2) { return; }
      refresh();
    }, intervalMs);
    // The person is back after a pause: the answer is not shown stale.
    window.addEventListener(USER_RETURN_EVENT, refresh);
    return () => { clearInterval(timer); window.removeEventListener(USER_RETURN_EVENT, refresh); };
  }, [enabled, intervalMs, projectId, refresh]);

  const loaded = Boolean(projectId) && snapshot.loaded;
  const answer = loaded ? snapshot.answer : null;
  return {
    services: answer?.services || [],
    // The project's own services (`services/*`), started or not.
    serviceNames: answer?.serviceNames || NO_NAMES,
    role: answer?.role || null,
    // Controls only once the role is known (a viewer never sees them flash); an older vfs-service without the route
    // (null answer) keeps them, as before.
    canRun: loaded && (answer ? answer.canRun !== false : true),
    unavailable: Boolean(answer?.servicesUnavailable),
    // Whether vfs-service answered with the services at all (an older one, or none configured: false).
    known: Boolean(answer),
    loaded,
    at: loaded ? snapshot.at : 0,
    refresh,
  };
}
